import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
    { value: "new", label: "Nouvelle" },
    { value: "contacted", label: "Contactée" },
    { value: "qualified", label: "Qualifiée" },
    { value: "won", label: "Signée" },
    { value: "lost", label: "Perdue" },
];

export default function AdminQuotes() {
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openId, setOpenId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/quotes");
            setQuotes(data);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const updateQuote = async (id, patch) => {
        try {
            const { data } = await api.patch(`/admin/quotes/${id}`, patch);
            setQuotes(quotes.map((q) => (q.id === id ? data : q)));
            toast.success("Demande mise à jour");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const deleteQuote = async (id) => {
        if (!window.confirm("Supprimer cette demande ?")) return;
        try {
            await api.delete(`/admin/quotes/${id}`);
            setQuotes(quotes.filter((q) => q.id !== id));
            toast.success("Demande supprimée");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="p-10" data-testid="admin-quotes-page">
            <span className="overline">Espace admin</span>
            <h1 className="mt-3 font-serif text-4xl text-brand-ink font-light">Demandes de devis</h1>

            {loading ? (
                <p className="mt-12 text-brand-sageDark">Chargement…</p>
            ) : error ? (
                <p className="mt-12 text-red-700">{error}</p>
            ) : quotes.length === 0 ? (
                <div className="mt-12 py-16 text-center bg-brand-bgalt border border-brand-border" data-testid="admin-quotes-empty">
                    <p className="font-serif text-xl text-brand-ink italic">Aucune demande pour l'instant.</p>
                </div>
            ) : (
                <div className="mt-10 space-y-3">
                    {quotes.map((q) => {
                        const isOpen = openId === q.id;
                        return (
                            <div key={q.id} className="bg-brand-bgalt border border-brand-border" data-testid={`quote-row-${q.id}`}>
                                <button
                                    onClick={() => setOpenId(isOpen ? null : q.id)}
                                    className="w-full p-5 flex items-center justify-between gap-4 text-left"
                                    data-testid={`quote-toggle-${q.id}`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className="font-serif text-lg text-brand-ink">{q.first_name} {q.last_name}</p>
                                        <p className="text-sm text-brand-sageDark mt-0.5 truncate">{q.project_type} · {q.location} · {new Date(q.created_at).toLocaleDateString("fr-FR")}</p>
                                    </div>
                                    <span className={`text-xs uppercase tracking-[0.16em] px-3 py-1 ${q.status === "new" ? "bg-brand-sage text-brand-bg" : "border border-brand-border text-brand-sageDark"}`}>
                                        {STATUSES.find((s) => s.value === q.status)?.label || q.status}
                                    </span>
                                    {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                </button>

                                {isOpen && (
                                    <div className="px-5 pb-6 pt-2 border-t border-brand-border grid md:grid-cols-2 gap-6">
                                        <dl className="space-y-3 text-sm">
                                            <Row label="Email" value={<a href={`mailto:${q.email}`} className="text-brand-sage hover:underline">{q.email}</a>} />
                                            <Row label="Téléphone" value={<a href={`tel:${q.phone}`} className="text-brand-sage hover:underline">{q.phone}</a>} />
                                            <Row label="Surface" value={q.surface || "—"} />
                                            <Row label="Budget" value={q.budget || "—"} />
                                            <Row label="Délai" value={q.timeline || "—"} />
                                            <div>
                                                <dt className="overline">Message</dt>
                                                <dd className="mt-1 text-brand-ink whitespace-pre-wrap font-light leading-relaxed bg-brand-bg p-3 border border-brand-border">{q.message}</dd>
                                            </div>
                                        </dl>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="overline block mb-2">Statut</label>
                                                <select
                                                    value={q.status}
                                                    onChange={(e) => updateQuote(q.id, { status: e.target.value })}
                                                    className="field-input bg-transparent"
                                                    data-testid={`quote-status-${q.id}`}
                                                >
                                                    {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="overline block mb-2">Notes internes</label>
                                                <textarea
                                                    rows={4}
                                                    defaultValue={q.admin_notes || ""}
                                                    onBlur={(e) => {
                                                        if (e.target.value !== (q.admin_notes || "")) {
                                                            updateQuote(q.id, { admin_notes: e.target.value });
                                                        }
                                                    }}
                                                    className="field-input field-textarea"
                                                    data-testid={`quote-notes-${q.id}`}
                                                    placeholder="Vos notes (enregistrées à la perte du focus)"
                                                />
                                            </div>
                                            <button
                                                onClick={() => deleteQuote(q.id)}
                                                data-testid={`quote-delete-${q.id}`}
                                                className="text-xs uppercase tracking-[0.2em] text-red-700 hover:underline flex items-center gap-2"
                                            >
                                                <Trash2 size={14} /> Supprimer
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between gap-4">
            <dt className="overline">{label}</dt>
            <dd className="text-brand-ink text-right">{value}</dd>
        </div>
    );
}
